import React, { useEffect } from 'react';
import { useStore } from '../../../../../../../stores/root';

// assets
import { faXmarkCircle } from '@fortawesome/pro-light-svg-icons';

// services
import ErrorService from '../../../../../../../services/general/error';
import GeneralService from '../../../../../../../services/general';

// props
import { IVesselRequisitionItemPricingResourceProps, IVesselRequisitionItemResourceProps, IVesselRequisitionItemResourceShortProps } from '../../../../../../../props/vessels/requisitions/item';

// components
import { DefaultButton, DetailsList, IColumn, IconButton, Panel, PanelType, PrimaryButton, SelectionMode, Spinner, SpinnerSize, Stack, TooltipHost } from '@fluentui/react';
import VesselRequisitionItemPricingStatus from '../../../../../../../manifests/status/vesselRequisitionItemPricing';
import Label from '../../../../../../typography/label';
import Text from '../../../../../../typography/text';
import UpdateVesselRequisitionItemPricingDetails from './details';

interface UpdateVesselRequisitionItemPricingProps {
    item?: IVesselRequisitionItemResourceProps;
    items?: IVesselRequisitionItemResourceShortProps[];
    onDismissed(refresh?: boolean, status?: string): void;
}

const UpdateVesselRequisitionItemPricing: React.FC<UpdateVesselRequisitionItemPricingProps> = (props: UpdateVesselRequisitionItemPricingProps) => {
    const { banner, user } = useStore();

    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [pricings, setPricings] = React.useState<Partial<IVesselRequisitionItemPricingResourceProps>[]>([]);

    const [activeSurface, setActiveSurface] = React.useState<string | undefined>();
    const [activePricing, setActivePricing] = React.useState<Partial<IVesselRequisitionItemPricingResourceProps> | undefined>();

    const [submitting, setSubmitting] = React.useState<boolean>(false);

    const columns: IColumn[] = [
        {
            key: 'item',
            name: 'Item',
            isMultiline: true,
            minWidth: 350,
            maxWidth: 350,
            onRender: (pricing: IVesselRequisitionItemPricingResourceProps) => {
                let size: JSX.Element | null = null;
                let weight: JSX.Element | null = null;

                // get size details
                const height = Number((pricing.item.goods.height || "").trim());
                const width = Number((pricing.item.goods.width || "").trim());
                const length = Number((pricing.item.goods.length || "").trim());
                const diameter = Number((pricing.item.goods.diameter || "").trim());

                if (diameter !== 0) {
                    size = <Text size={'small'}>Diameter: {GeneralService.getNumberWithSeparator(Number(diameter))} cm</Text>
                } else if (height !== 0 && width !== 0 && length !== 0) {
                    size = <Text size={'small'}>Size (P x L x T): {GeneralService.getNumberWithSeparator(Number(length))} cm x {GeneralService.getNumberWithSeparator(Number(width))} cm x {GeneralService.getNumberWithSeparator(Number(height))} cm</Text>
                }

                // get weight details
                const weightAmount = Number((pricing.item.goods.weightAmount || "").trim());

                if (weightAmount !== 0) {
                    weight = <Text size={'small'}>Weight: {GeneralService.getNumberWithSeparator(Number(weightAmount))} {pricing.item.goods.weightAmount}</Text>
                }

                return <Stack styles={{ root: { padding: '8px 0px' } }}>
                    <Label size={'small'}>{pricing.item.goods.name}</Label>
                    {size}
                    {weight}
                </Stack>
            }
        },
        {
            key: 'purpose',
            name: 'Purpose / Notes',
            isMultiline: true,
            minWidth: 200,
            maxWidth: 200,
            onRender: (pricing: IVesselRequisitionItemPricingResourceProps) => {
                return <Stack styles={{ root: { padding: '8px 0px' } }}>
                    <Text size={'small'}>{pricing.item.purpose || "-"}</Text>
                </Stack>
            }
        },
        {
            key: 'quantity',
            name: 'Quantity',
            isMultiline: true,
            minWidth: 125,
            maxWidth: 125,
            onRender: (pricing: IVesselRequisitionItemPricingResourceProps) => {
                return <Stack styles={{ root: { padding: '8px 0px' } }}>
                    <Text size={'small'}>{GeneralService.getNumberWithSeparator(Number(pricing.item.quantityAmount))} {pricing.item.quantityUnit.name}</Text>
                </Stack>
            }
        },
        {
            key: 'price',
            name: 'Price per item',
            isMultiline: true,
            minWidth: 125,
            maxWidth: 125,
            onRender: (pricing: IVesselRequisitionItemPricingResourceProps) => {
                return <Stack styles={{ root: { padding: '8px 0px' } }}>
                    <Text size={'small'}>-</Text>
                </Stack>
            }
        },
        {
            key: 'action',
            name: '',
            isMultiline: true,
            minWidth: 125,
            maxWidth: 125,
            onRender: (pricing: IVesselRequisitionItemPricingResourceProps) => {
                return <Stack>
                    <TooltipHost content={"Update price"}>
                        <IconButton iconProps={{ iconName: "Edit" }} onClick={() => {
                            setActiveSurface('pricing.update');
                            setActivePricing(pricing);
                        }} />
                    </TooltipHost>
                </Stack>
            }
        }
    ];

    useEffect(() => {
        init();
    }, []);

    const init = async () => {
        try {
            const _pricings: Partial<IVesselRequisitionItemPricingResourceProps>[] = [];
            const _items = props.item ? [props.item] : props.items || [];

            _items.forEach((item) => {
                _pricings.push({
                    id: GeneralService.makeid(32, false, '__SYS_NEW'),
                    itemId: item.id,
                    item,
                    status: VesselRequisitionItemPricingStatus.find((s) => s.isDefault)?.key || "",
                    price: "0",
                    totalPrice: "0",
                    nettPrice: "0",
                    invoicePrice: "0",
                    finalPrice: "0",
                    includePPn: false,
                    includePPh: false,
                    ppnPercentage: "0",
                    pphPercentage: "0",
                    ppn: "0",
                    pph: "0"
                });
            });

            setPricings([..._pricings]);
            setLoaded(true);
        } catch (e) {
            banner.add({
                key: 'get_requisition_items_error',
                variant: 'error',
                icon: faXmarkCircle,
                text: `Failed to get requisition items. Error: ${ErrorService.getMessage(e)}`
            });
            props.onDismissed();
        }
    }

    const _onSubmit = async () => {
        try {
            setSubmitting(true);

            const fd = new FormData();

            props.onDismissed(true);
        } catch (e) {
            setSubmitting(false);
        }
    }

    return <Panel headerText={'Update Requisition Item Price'}
        isOpen={true}
        type={PanelType.large}
        onDismiss={() => props.onDismissed(false)}
        isFooterAtBottom={true}
        onRenderFooterContent={() => {
            return <Stack horizontal tokens={{ childrenGap: 10 }}>
                {
                    !submitting ? (
                        <>
                            <PrimaryButton text={"Approve"} onClick={_onSubmit} />
                            <DefaultButton text={"Cancel"} onClick={() => { props.onDismissed(false) }} />
                        </>
                    ) : null
                }
                {submitting ? <Spinner size={SpinnerSize.medium} labelPosition={"right"} label={"Approving requisition items ..."} /> : null}
            </Stack>;
        }}>
        <Stack tokens={{ childrenGap: 15 }}>
            {!loaded ? <Stack horizontalAlign={"baseline"}><Spinner size={SpinnerSize.medium} labelPosition={"right"} label={"Preparing form ..."} /></Stack> : null}
            {
                loaded ? <>
                    <DetailsList items={pricings}
                        columns={columns}
                        selectionMode={SelectionMode.none} />
                </> : null
            }
            {activeSurface === 'pricing.update' && activePricing ? <UpdateVesselRequisitionItemPricingDetails pricing={activePricing} onDismissed={(data) => {
                if (data) {
                    const _pricings = pricings;
                    const idx = _pricings.findIndex((p) => p.id === data.id);
                    if (idx > -1) {
                        _pricings[idx] = data;
                    }

                    setPricings([..._pricings]);
                }

                setActivePricing(undefined);
                setActiveSurface(undefined);
            }} /> : null}
        </Stack>
    </Panel>
};

export default UpdateVesselRequisitionItemPricing;
